import { useEffect, useRef, useState } from "react";
import { ArrowRight, MoreHorizontal } from "lucide-react";
import { TABS, TAB_TOTAL } from "@/data/tabs";
import { useWindows } from "@/os/window/context";
import { useSettings } from "@/os/state/settings";
import { useAchievements } from "@/os/state/achievements";
import { play } from "@/lib/sound";

export function TabsBrowser() {
    const { open } = useWindows();
    const { sliders } = useSettings();
    const { trackTab, trackEgg } = useAchievements();
    const [activeId, setActiveId] = useState(TABS[0].id);
    const [hiddenRevealed, setHiddenRevealed] = useState(false);
    const [closedIds, setClosedIds] = useState<string[]>([]);

    const base = TABS.filter((t) => (t.curiosityLocked ? sliders.curiosity >= 70 : true)).filter((t) => (t.hidden ? hiddenRevealed : true));
    const visible = base.filter((t) => !closedIds.includes(t.id));
    const active = visible.find((t) => t.id === activeId) ?? visible[0];

    const select = (id: string) => {
        setActiveId(id);
        play("click");
        trackTab(id);
        const t = TABS.find((x) => x.id === id);
        if (t?.hidden) trackEgg("egg_hidden_tab");
    };

    const closeTab = (id: string) => {
        play("close");
        setClosedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
        if (activeId === id) {
            const next = visible.find((t) => t.id !== id);
            setActiveId(next ? next.id : "");
        }
    };

    const restoreTabs = () => {
        play("open");
        setClosedIds([]);
        setActiveId(TABS[0].id);
    };

    const reopenLast = () => {
        if (!closedIds.length) return;
        play("open");
        const id = closedIds[closedIds.length - 1];
        setClosedIds((prev) => prev.slice(0, -1));
        setActiveId(id);
    };

    const reopenRef = useRef(reopenLast);
    reopenRef.current = reopenLast;
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.shiftKey && (e.key === "T" || e.key === "t")) {
                e.preventDefault();
                reopenRef.current();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <div data-testid="tabs-browser">
            <div className="os-scroll flex items-center gap-1 overflow-x-auto pb-1" role="tablist" aria-label="37 open tabs">
                {visible.map((t) => (
                    <div
                        key={t.id}
                        role="tab"
                        tabIndex={0}
                        aria-selected={active?.id === t.id}
                        data-testid={`browser-tab-${t.id}`}
                        onClick={() => select(t.id)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                select(t.id);
                            }
                        }}
                        className={`group flex flex-none cursor-pointer items-center gap-1.5 rounded-t-lg border border-b-0 px-3 py-1.5 font-mono text-[10.5px] transition-colors ${
                            active?.id === t.id ? "border-ink bg-paper text-ink" : "border-sand bg-sandsoft/70 text-inksoft hover:bg-sandsoft"
                        }`}
                    >
                        <span>
                            {t.curiosityLocked ? "+ " : ""}
                            {t.title}
                        </span>
                        <button
                            data-testid={`browser-tab-close-${t.id}`}
                            aria-label={`Close tab: ${t.title}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                closeTab(t.id);
                            }}
                            className="rounded px-0.5 text-inkfaint opacity-60 transition-all hover:scale-125 hover:bg-coralsoft hover:text-coral group-hover:opacity-100"
                        >
                            ×
                        </button>
                    </div>
                ))}
                {!hiddenRevealed && (
                    <button
                        data-testid="browser-tab-more"
                        aria-label="Reveal one more tab"
                        onClick={() => setHiddenRevealed(true)}
                        className="flex-none rounded-full border border-sand bg-paper px-2 py-1 text-inkfaint hover:border-ink hover:text-ink"
                    >
                        <MoreHorizontal size={14} />
                    </button>
                )}
                {closedIds.length > 0 && visible.length > 0 && (
                    <>
                        <button
                            data-testid="browser-reopen-last"
                            className="chip flex-none hover:bg-sandsoft"
                            onClick={reopenLast}
                            title="or press Ctrl+Shift+T"
                        >
                            ↩ reopen last
                        </button>
                        <button data-testid="browser-restore-tabs" className="chip flex-none hover:bg-sandsoft" onClick={restoreTabs}>
                            restore all {closedIds.length}
                        </button>
                    </>
                )}
            </div>
            {visible.length === 0 ? (
                <div
                    className="mt-4 min-h-[190px] rounded-xl border-2 border-dashed border-inkfaint bg-sandsoft/40 p-6 text-center"
                    data-testid="browser-empty"
                >
                    <p className="font-display text-lg font-bold text-ink">zero tabs. a personal record.</p>
                    <p className="mt-2 text-[13px] text-inksoft">
                        the browser has never been this quiet. unsettling, honestly. don't worry — they grow back.
                    </p>
                    <button data-testid="browser-restore-all" className="btn btn-blue btn-sm mt-4" onClick={restoreTabs}>
                        reopen all 37 tabs
                    </button>
                </div>
            ) : (
                <div role="tabpanel" className="mt-4 min-h-[190px] rounded-xl border border-sand bg-paper p-4" data-testid="browser-content">
                    <h3 className="font-display text-lg font-bold text-ink">{active?.title}</h3>
                    {active?.body.map((p, i) => (
                        <p key={i} className="mt-2 text-[13px] leading-relaxed text-inksoft">
                            {p}
                        </p>
                    ))}
                    {active?.projectId && (
                        <button data-testid="tabs-open-project" className="btn btn-blue btn-sm mt-4" onClick={() => open(`project:${active.projectId}`)}>
                            open the {active.projectId.replace(/-/g, "_")} project <ArrowRight size={14} />
                        </button>
                    )}
                </div>
            )}
            <p className="mt-3 font-mono text-[10.5px] text-inkfaint">
                showing {visible.length} of {TAB_TOTAL} tabs — the rest are load-bearing.
                {closedIds.length > 0
                    ? " (closed tabs grow back when you reopen the browser.)"
                    : sliders.curiosity >= 70
                      ? " (curiosity unlocked the hidden ones.)"
                      : " (some tabs unlock at curiosity ≥ 70 in personality_settings.exe.)"}
            </p>
        </div>
    );
}
